import { formatIsoDateTimeToDdMmYyyy } from "@/utils/DateFormatter";

const SuitabilityTestLimitReachedCode = "SUITABILITY_TEST_LIMIT_REACHED";

const SuitabilityTestErrorMessages: Record<string, string> =
{
    NO_DRAFT_SUITABILITY_TEST: "No hay un test de idoneidad pendiente de firmar. Vuelve a revisarlo y envíalo de nuevo.",
    SIGNATURE_NOT_VALIDATED: "La firma no se ha validado. Comprueba el código SMS e inténtalo de nuevo.",
    SUITABILITY_TEST_RESULTS_UNAVAILABLE: "No se ha podido calcular el perfil del test. Inténtalo de nuevo.",
    SUITABILITY_TEST_VERSION_OUTDATED: "El cuestionario ha cambiado. Vuelve a cargar el test para hacerlo con la versión actual.",
};

const SuitabilityTestForbiddenMessage = "No puedes realizar el test de idoneidad de este interviniente.";

const SuitabilityTestFallbackMessage = "Se ha producido un error que impide continuar con el proceso.";

interface SuitabilityTestErrorBody
{
    code?: string;
    Code?: string;
    message?: string;
    Message?: string;
    nextAllowedAt?: unknown;
    NextAllowedAt?: unknown;
}

function readErrorBody(error: unknown): SuitabilityTestErrorBody | undefined
{
    if(error == null || typeof error !== "object")
    {
        return undefined;
    }

    const data = (error as { response?: { data?: unknown } }).response?.data;
    if(data == null || typeof data !== "object")
    {
        return undefined;
    }

    return data as SuitabilityTestErrorBody;
}

function readErrorCode(error: unknown): string | undefined
{
    const body = readErrorBody(error);
    const code = body?.code ?? body?.Code;
    if(typeof code !== "string")
    {
        return undefined;
    }

    const trimmed = code.trim();
    return trimmed === "" ? undefined : trimmed;
}

function readHttpStatus(error: unknown): number | undefined
{
    if(error == null || typeof error !== "object")
    {
        return undefined;
    }

    const responseStatus = (error as { response?: { status?: unknown } }).response?.status;
    const ownStatus = (error as { status?: unknown }).status;
    const status = typeof responseStatus === "number" ? responseStatus : ownStatus;
    return typeof status === "number" ? status : undefined;
}

function readServerMessage(error: unknown, code?: string): string | undefined
{
    const body = readErrorBody(error);
    const message = body?.message ?? body?.Message;
    if(typeof message !== "string")
    {
        return undefined;
    }

    const trimmed = message.trim();
    if(trimmed === "" || trimmed === code)
    {
        return undefined;
    }

    return trimmed;
}

export function isSuitabilityLimitReachedError(error: unknown): boolean
{
    return readErrorCode(error) === SuitabilityTestLimitReachedCode;
}

function resolveLimitReachedMessage(error: unknown): string
{
    const body = readErrorBody(error);
    const formatted = formatIsoDateTimeToDdMmYyyy(body?.nextAllowedAt ?? body?.NextAllowedAt);
    if(formatted !== "—")
    {
        return `Has firmado dos tests de idoneidad en los últimos 30 días. Podrás firmar otro a partir del ${formatted}.`;
    }

    return "Este interviniente no puede firmar un test de idoneidad.";
}

/**
 * Texto de usuario para un error del test de idoneidad.
 * Los códigos conocidos y el 403 (interviniente prestado) tienen mensaje propio.
 * Si el código no está mapeado, se usa el mensaje del servidor. Nunca se muestra el código en crudo.
 */
export function resolveSuitabilityTestError(error: unknown, fallback?: string): string
{
    const code = readErrorCode(error);
    if(code === SuitabilityTestLimitReachedCode)
    {
        return resolveLimitReachedMessage(error);
    }

    const mapped = code ? SuitabilityTestErrorMessages[code] : undefined;
    if(mapped)
    {
        return mapped;
    }

    if(readHttpStatus(error) === 403)
    {
        return SuitabilityTestForbiddenMessage;
    }

    return readServerMessage(error, code) ?? fallback ?? SuitabilityTestFallbackMessage;
}
